import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckIcon,
  MoonIcon,
  SunIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/solid';
import MobileLayout from '../../components/MobileLayout';
import { useTheme } from '../../contexts/ThemeContext';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

interface ThemeOption {
  value: 'dark' | 'light' | 'auto';
  label: string;
  description: string;
  icon: React.ElementType;
}

const getThemeOptions = (t: any): ThemeOption[] => [
  {
    value: 'dark',
    label: t('settings.theme.dark', 'Dark'),
    description: t('settings.theme.darkDescription', 'Always use dark theme'),
    icon: MoonIcon,
  },
  {
    value: 'light',
    label: t('settings.theme.light', 'Light'),
    description: t('settings.theme.lightDescription', 'Always use light theme'),
    icon: SunIcon,
  },
  {
    value: 'auto',
    label: t('settings.theme.auto', 'Auto'),
    description: t('settings.theme.autoDescription', 'Match system settings'),
    icon: ComputerDesktopIcon,
  },
];

const ThemeSettings: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const themeOptions = getThemeOptions(t);

  const handleThemeChange = async (newTheme: 'dark' | 'light' | 'auto') => {
    setTheme(newTheme);

    // Save to database
    try {
      await axios.put('/users/settings', { theme: newTheme });
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  };

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-light-border dark:border-dark-border">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/profile')}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-light-surface dark:hover:bg-dark-surface"
            >
              <ArrowLeftIcon className="w-5 h-5 text-light-text dark:text-dark-text" />
            </button>
            <h1 className="ml-4 text-xl font-semibold text-light-text dark:text-dark-text">
              {t('settings.theme.title', 'Theme')}
            </h1>
          </div>
        </div>

        {/* Theme Options */}
        <div className="flex-1 overflow-y-auto">
          <div className="py-2">
            {themeOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => handleThemeChange(option.value)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-light-surface dark:hover:bg-dark-surface transition-colors"
                >
                  <div className="flex items-center">
                    <Icon className="w-6 h-6 text-light-text-secondary dark:text-dark-text-secondary mr-4" />
                    <div className="text-left">
                      <div className="text-light-text dark:text-dark-text font-medium">
                        {option.label}
                      </div>
                      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                        {option.description}
                      </div>
                    </div>
                  </div>
                  {theme === option.value && <CheckIcon className="w-5 h-5 text-primary-500" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Info message */}
        <div className="px-6 py-4 border-t border-light-border dark:border-dark-border">
          <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary text-center">
            {theme === 'auto'
              ? t(
                  'settings.theme.autoInfo',
                  'Theme will automatically switch based on your device settings',
                )
              : t('settings.theme.currentTheme', 'Using {{theme}} theme', { theme })}
          </p>
        </div>
      </div>
    </MobileLayout>
  );
};

export default ThemeSettings;
